import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Gift, Users, CheckCircle, Clock, ArrowLeft, Trophy, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminReferrals() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const getAuth = () => {
    const auth = localStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
      return null;
    }
    return { Authorization: "Basic " + auth };
  };

  useEffect(() => {
    const headers = getAuth();
    if (!headers) return;
    axios.get(API + "/api/admin/referral-stats", { headers })
      .then(res => setStats(res.data))
      .catch(err => {
        if (err.response?.status === 401) {
          localStorage.removeItem("adminAuth");
          navigate("/admin");
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const handleGenerateCodes = async () => {
    if (!window.confirm("Générer des codes parrainage pour tous les clients qui n'en ont pas ?")) return;
    const headers = getAuth();
    if (!headers) return;
    setGenerating(true);
    try {
      const res = await axios.post(API + "/api/admin/generate-referral-codes", {}, { headers });
      toast.success(res.data.message);
      window.location.reload();
    } catch (error) {
      toast.error("Erreur lors de la génération");
    }
    setGenerating(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const parrains = stats?.top_parrains || [];
  const referrals = stats?.referrals || [];

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate("/admin/dashboard")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Gift className="w-6 h-6 text-pink-500" />
              Programme Parrainage
            </h1>
          </div>
          <Button onClick={handleGenerateCodes} disabled={generating} className="bg-pink-500 hover:bg-pink-600">
            <RefreshCw className={"w-4 h-4 mr-2 " + (generating ? "animate-spin" : "")} />
            {generating ? "Génération..." : "Générer codes manquants"}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total parrainages</p>
                <p className="text-3xl font-bold">{stats?.total_referrals || 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Validés</p>
                <p className="text-3xl font-bold text-green-600">{stats?.validated_referrals || 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">En attente</p>
                <p className="text-3xl font-bold text-amber-600">{stats?.pending_referrals || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                Top Parrains
              </CardTitle>
            </CardHeader>
            <CardContent>
              {parrains.length === 0 && <p className="text-center text-slate-500 py-8">Aucun parrainage</p>}
              <div className="space-y-3">
                {parrains.map((p, i) => (
                  <div key={p.email} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full flex items-center justify-center font-bold bg-pink-100 text-pink-700">{i + 1}</span>
                      <div>
                        <p className="font-medium">{p.name}</p>
                        <p className="text-sm text-slate-500">{p.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-pink-600">{p.count} filleul(s)</p>
                      <code className="text-xs bg-pink-100 text-pink-700 px-2 py-0.5 rounded">{p.code}</code>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-pink-500" />
                Derniers parrainages
              </CardTitle>
            </CardHeader>
            <CardContent>
              {referrals.length === 0 && <p className="text-center text-slate-500 py-8">Aucun parrainage</p>}
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {referrals.map((r, i) => (
                  <div key={i} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{r.filleul_name}</span>
                      <Badge className={r.status === "validated" ? "bg-green-500" : "bg-amber-500"}>
                        {r.status === "validated" ? "Validé" : "En attente"}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-500">
                      Parrainé par: <span className="font-medium text-slate-700">{r.parrain_name}</span>
                    </p>
                    {r.date && <p className="text-xs text-slate-400">{format(new Date(r.date), "d MMM yyyy", { locale: fr })}</p>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="mt-6 border-pink-200 bg-pink-50">
          <CardContent className="p-6">
            <h3 className="font-bold text-pink-700 mb-3">Règles du programme</h3>
            <ul className="space-y-1 text-sm text-pink-700">
              <li>Parrain : gagne 5€ après la 1ère livraison du filleul</li>
              <li>Filleul : reçoit 5€ dès son inscription</li>
              <li>Maximum 5 parrainages par client</li>
              <li>Anti-fraude : email et adresse différents, nouveau client uniquement</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
